// Application.js
import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Grid, Container, Box, Divider, Typography, Button } from "@mui/material";
import { initialValues, fieldLabels, sectionComponents } from './formConstants';


function getLocalStorage(name) {
    const saved = localStorage.getItem("formValues");
    return saved ? JSON.parse(saved) : null;
}

const Application = () => {
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    const saveFormValues = (values) => {
        localStorage.setItem("formValues", JSON.stringify(values));
    }

    const getInitialValuesOrCookieValues = () => {
        let savedValues = getLocalStorage("formValues")
        return savedValues && savedValues["firstName"] != "" ? savedValues : initialValues;
    }

    const getValidationSchema = (values, currentStep) => {
        const currentSectionFields = Object.keys(fieldLabels).filter(
            (key) => fieldLabels[key].section === sectionComponents[currentStep].sectionNumber);
        const schemaShape = {};

        currentSectionFields.forEach((key) => {
            if (fieldLabels[key].required ||
                (fieldLabels[key].dependsOn != null &&
                    (values[fieldLabels[key].dependsOn] === 'Yes') || (fieldLabels[key].dependsOnRequired != null &&
                        fieldLabels[key].dependsOnRequired.includes(values[fieldLabels[key].dependsOn])))) {
                schemaShape[key] = Yup.string().required(
                    `${fieldLabels[key].visualName || key} is required`
                );
            }
        });
        return Yup.object().shape(schemaShape);
    };


    const isLastStep = step === sectionComponents.length - 1;

    const handleSubmit = async (values, actions) => {
        const errors = await actions.validateForm();
        const currentSectionFields = Object.keys(fieldLabels).filter(
            (key) => fieldLabels[key].section === sectionComponents[step].sectionNumber
        );
        const hasErrors = currentSectionFields.some(field => errors[field]);

        if (hasErrors) {
            const newTouched = currentSectionFields.reduce((acc, key) => {
                if (!actions.touched[key] && errors[key]) {
                    acc[key] = true;
                }
                return acc;
            }, {});
            actions.setTouched({ ...actions.touched, ...newTouched });
            actions.setSubmitting(false);
        } else if (!isLastStep) {
            saveFormValues(values);
            setStep(step + 1);
            actions.setSubmitting(false);
        } else {
            console.log("Form submitted:", values);
            try {
                const resp = await fetch('https://fq9g7sk4v5.execute-api.us-east-2.amazonaws.com/dev/fill-pdf', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(values)
                });
                if (resp.ok) {
                    setSubmitted(true);
                    saveFormValues(initialValues);
                    return;      // skip the rest
                }
                throw new Error('POST to lambda failed');

            }
            catch (err) {
                console.error(err);
                actions.setSubmitting(false);
            }


            actions.setSubmitting(false);
        }
    };

    if (submitted) {
        window.scrollTo(0, 0);
        return (
            <Container>
                <Grid container textAlign="center" mt={10} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '35vh',
                    textAlign: 'center',
                }}>
                    <Typography variant="h4" gutterBottom>Thank you!</Typography>
                    <Typography>Your application has been submitted successfully. We will be reaching out to you soon.</Typography>
                </Grid>
            </Container>
        );
    }

    return (
        <Container maxWidth={false}>
            <Box display="flex" justifyContent="center" className="lg-p-top"></Box>
            <Typography variant="h4" align="center" gutterBottom>
                Public Defender's Application Form
            </Typography>
            <Formik
                initialValues={getInitialValuesOrCookieValues()}
                validate={async (values) => {
                    // 1. Build the schema for this step
                    const schema = getValidationSchema(values, step);

                    try {
                        // 2. Synchronously validate the current values
                        await schema.validate(values, { abortEarly: false });
                        return {}; // no errors
                    } catch (yupError) {
                        // 3. Aggregate all errors into Formik’s expected shape
                        const errors = {};
                        if (yupError.inner) {
                            yupError.inner.forEach((err) => {
                                if (!errors[err.path]) {
                                    errors[err.path] = err.message;
                                }
                            });
                        }
                        return errors;
                    }
                }} onSubmit={handleSubmit}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({
                    handleSubmit,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    values,
                    errors,
                    touched,
                }) => (
                    <Form onSubmit={handleSubmit}>
                        <Divider style={{ margin: "50px 0" }} />
                        <Typography variant="h6" align="center" gutterBottom>
                            {sectionComponents[step].title}
                        </Typography>

                        {/* Render the section based on the current step */}
                        {sectionComponents[step].component(
                            values,
                            handleChange,
                            handleBlur,
                            errors,
                            touched,
                            isSubmitting
                        )}

                        <Divider style={{ margin: "50px 0" }} />

                        {/* Navigation Buttons */}
                        <Grid container spacing={2} justifyContent="space-between">
                            {(
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            saveFormValues(values);
                                            setStep(step - 1);
                                        }
                                        }
                                        disabled={step === 0}
                                    >
                                        Back
                                    </Button>
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                >
                                    {isLastStep ? (isSubmitting ? "Submitting..." : "Submit Application") : "Next"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};
export default Application;
